import { Pie, PieChart, ResponsiveContainer, Sector } from "recharts";
import { ModulesStatesEnum } from "../../types/enums/modules-states.enum";
import { moduleColors } from "@/styles/modules-colors";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
// @ts-ignore
import tw from "twin.macro";

import { t } from "i18next";

const COLORS: Record<ModulesStatesEnum, string> = {
  [ModulesStatesEnum.UNFOLDED]: moduleColors.greenBase,
  [ModulesStatesEnum.FOLDED]: moduleColors.greenBase,
  [ModulesStatesEnum.IN_PROGRESS]: moduleColors.amberBase,
  [ModulesStatesEnum.UNDER_MAINTENANCE]: moduleColors.greyBase,
  [ModulesStatesEnum.FAULTY]: moduleColors.redBase,
  [ModulesStatesEnum.UP]: moduleColors.greenBase,
  [ModulesStatesEnum.LOCKED]: moduleColors.greyBase,
};

function CustomPieSector(props: any) {
  const { name }: { name: ModulesStatesEnum } = props;
  return <Sector {...props} fill={COLORS[name]} />;
}

function AmberPieSector(props: any) {
  return <Sector {...props} fill="rgb(255, 212, 59)" width={80} height={80} />;
}

function WhitePieSector(props: any) {
  return <Sector {...props} fill="white" width={80} height={80} />;
}

const RenderPieMultiParts = (
  displayedPies: { name: ModulesStatesEnum; value: number }[]
) => (
  <Pie
    data={displayedPies}
    dataKey="value"
    innerRadius={35}
    outerRadius={43}
    startAngle={90}
    endAngle={-270}
    paddingAngle={3}
    isAnimationActive={true}
    animationDuration={800}
    stroke="none"
    shape={<CustomPieSector />}
  />
);

/** Chrono inversé : fond jaune plein, secteur blanc qui grandit et "efface" le jaune. */
function RenderChronoPie(progressPercent: number, eraseAngle: number) {
  const isFullyErased = progressPercent >= 100;

  return (
    <div>
      {/* 1. Fond jaune (cercle plein) */}
      <Pie
        data={[{ value: 1 }]}
        dataKey="value"
        innerRadius="70%"
        outerRadius="95%"
        startAngle={90}
        endAngle={-270}
        paddingAngle={0}
        isAnimationActive={false}
        stroke="none"
        width="100%"
        height="100%"
        shape={<AmberPieSector />}
      />
      {/* 2. Secteur blanc qui efface au fur et à mesure (de 0° à 360°) */}
      {eraseAngle > 0 && (
        <Pie
          data={[{ value: 1 }]}
          dataKey="value"
          innerRadius="70%"
          outerRadius="95%"
          startAngle={90}
          endAngle={isFullyErased ? -270 : 90 - eraseAngle}
          paddingAngle={0}
          isAnimationActive={true}
          animationDuration={800}
          stroke="none"
          width="100%"
          height="100%"
          shape={<WhitePieSector />}
        />
      )}
    </div>
  );
}

/** Rendu donut pour une action en cours (chrono inversé : jaune effacé par le blanc). */
export function ChronoPieChart({
  progressPercent,
}: {
  progressPercent: number;
}) {
  /** Angle du secteur blanc qui "efface" le jaune (0° → 360° quand progress 0% → 100%). */
  const eraseAngle = (progressPercent / 100) * 360;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart width="100%" height="100%">
        {RenderChronoPie(progressPercent, eraseAngle)}
      </PieChart>
    </ResponsiveContainer>
  );
}

/** Rendu donut multi-parties + texte central pour l’état idle (pas d’action en cours). */
function RenderIdlePie(pieData: { name: ModulesStatesEnum; value: number }[]) {
  const filteredDisplayedPies = pieData.filter((d) => d.value > 0);
  if (filteredDisplayedPies.length === 0) return null;

  return (
    <PieChart>
      {RenderPieMultiParts(filteredDisplayedPies)}
      <text
        x="50%"
        y="48%"
        textAnchor="middle"
        dominantBaseline="middle"
        tw="fill-[#383b45] text-xl font-bold p-1"
      >
        {filteredDisplayedPies[0].value}%
      </text>
      <text
        x="48%"
        y="62%"
        textAnchor="middle"
        dominantBaseline="middle"
        tw="fill-slate-500 text-xs font-semibold"
      >
        {t(`modulesStates.${filteredDisplayedPies[0].name}`)}
      </text>
    </PieChart>
  );
}

/** Donut état idle : répartition des modules par état. */
export function AreaStatusDonut({
  pieData,
}: {
  pieData: { name: ModulesStatesEnum; value: number }[];
}) {
  return <ResponsiveContainer>{RenderIdlePie(pieData)}</ResponsiveContainer>;
}

/** Donut "arrêt d'urgence" : anneau rouge plein + libellé centré. */
export function EmergencyStopDonut({ label }: { label: string }) {
  return (
    <div tw="relative w-full h-full">
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={[{ value: 1 }]}
            dataKey="value"
            innerRadius={35}
            outerRadius={43}
            startAngle={90}
            endAngle={-270}
            paddingAngle={0}
            isAnimationActive={false}
            stroke="none"
            fill={moduleColors.redBase}
          />
        </PieChart>
      </ResponsiveContainer>
      <div tw="absolute inset-0 flex items-center justify-center px-4 text-center">
        <span tw="text-[9px] font-bold leading-tight text-modules-redBase">
          {label}
        </span>
      </div>
    </div>
  );
}
